import React, { useState, useEffect, useRef } from "react";
import { X, Send, ArrowLeft, ChevronLeft, ChevronRight, Info } from "lucide-react";
import Disclaimer from "../Legal/Disclaimer";

// ─── CONSTANTS ─────────────────────────────────────────────────────────────
export const SHOP_CATEGORIES = [
  { id: "All", label: "All" },
  { id: "oversize_signs", label: "Signs" },
  { id: "lights_strobes", label: "Lights" },
  { id: "roof_racks_mounts", label: "Mounts" },
  { id: "cb_radios_comm", label: "Electronics" },
  { id: "flags_safety", label: "Safety Gear" },
  { id: "height_poles", label: "High-Poles" },
  { id: "vehicles", label: "Cars & Trucks" },
  { id: "heavy_equipment", label: "Heavy Equipment" },
  { id: "complete_kits", label: "Kits" },
  { id: "bundles", label: "Bundles" },
  { id: "personal_items", label: "Personal Items" },
  { id: "other", label: "Other" },
];

export const QUICK_QUESTIONS = [
  "Is this still available?",
  "Any damage or issues?",
  "Can you send more photos?",
  "What's your best price?",
];

export const API_URL = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");

// ─── FORMATTERS ────────────────────────────────────────────────────────────
export const safeParse = (data, fallback) => {
  if (!data) return fallback;
  if (typeof data === "object") return data;
  try { return JSON.parse(data); } catch (e) { return fallback; }
};

export const formatName = (userObj) => {
  if (!userObj) return "Seller";
  if (typeof userObj === "object") {
    const first = userObj.first_name?.trim();
    const last = userObj.last_name?.trim();
    if (first && last) return `${first.charAt(0).toUpperCase() + first.slice(1)} ${last.charAt(0).toUpperCase()}.`;
    if (first) return first.charAt(0).toUpperCase() + first.slice(1);
    if (userObj.username) return userObj.username;
    if (userObj.email) return userObj.email.split("@")[0];
  }
  if (typeof userObj === "string") {
    const parts = userObj.split("@")[0].split(/[\s._-]+/).filter(Boolean);
    if (parts.length > 1) return `${parts[0].charAt(0).toUpperCase() + parts[0].slice(1)} ${parts[parts.length - 1].charAt(0).toUpperCase()}.`;
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
  }
  return "Seller";
};

export const categoryLabel = (id) => SHOP_CATEGORIES.find((c) => c.id === id)?.label || id;
export const isVerifiedListing = (item) => Boolean(item?.is_ppca_verified);

export const getInitials = (formattedName) => {
  if (!formattedName || formattedName === "Seller") return "SE";
  const parts = formattedName.split(" ");
  if (parts.length > 1) return (parts[0][0] + parts[1][0]).toUpperCase();
  return formattedName.substring(0, 2).toUpperCase();
};

export const timeAgo = (dateStr) => {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString([], { month: "short", day: "numeric" });
};

export const toTitleCase = (str) => {
  if (!str) return "";
  return str.toLowerCase().split(" ").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
};

// ─── THEME TOKENS (shared across marketplace pages) ───────────────────────
export const buildTheme = (isDarkMode) => {
  const D = isDarkMode;
  return {
    D,
    bg: D ? "#0d1f3c" : "#f8fafc",
    panelBg: D ? "#142a4d" : "#ffffff",
    midBg: D ? "#1a3358" : "#f1f5f9",
    border: D ? "rgba(255,255,255,0.10)" : "#e2e8f0",
    text: D ? "#f0f4f8" : "#0f172a",
    textMid: D ? "rgba(240,244,248,0.75)" : "#475569",
    textSoft: D ? "rgba(240,244,248,0.50)" : "#94a3b8",
    inputBg: D ? "rgba(255,255,255,0.06)" : "#ffffff",
    amber: "#f59e0b",
    amberL: "#fbbf24",
  };
};

// ─── STATUS TICK (read receipts) ───────────────────────────────────────────
export function StatusTick({ isRead, size = 14 }) {
  const color = isRead ? "#3BA3FF" : "#A0A0A0";
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 0, flexShrink: 0 }}>
      <svg width={size} height={size} viewBox="0 0 13 13" fill="none">
        <polyline points="2,7 5,10 11,3" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      {isRead && (
        <svg width={size} height={size} viewBox="0 0 13 13" fill="none" style={{ marginLeft: -7 }}>
          <polyline points="2,7 5,10 11,3" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </span>
  );
}

// ─── SPECIFICATIONS BLOCK ───────────────────────────────────────────────────
export function ListingAttributes({ item }) {
  const extras = safeParse(item.extra_fields, {});
  const compStates = safeParse(item.compatible_states, []);
  const escTypes = safeParse(item.escort_types, []);

  const attributes = [];
  if (item.shipping) attributes.push({ label: "Shipping", value: item.shipping });
  if (item.negotiable) attributes.push({ label: "Price", value: "Negotiable / OBO" });
  if (compStates.length > 0) attributes.push({ label: "States", value: compStates.join(", ") });
  if (escTypes.length > 0) attributes.push({ label: "Escort Types", value: escTypes.join(", ") });

  Object.entries(extras).forEach(([k, v]) => {
    if (!v || (Array.isArray(v) && v.length === 0)) return;
    const label = k.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
    const value = Array.isArray(v) ? v.join(", ") : typeof v === "boolean" ? (v ? "Yes" : "No") : v;
    attributes.push({ label, value });
  });

  if (attributes.length === 0) return null;

  return (
    <div className="mt-4 mb-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
      <h4 className="text-[11px] font-bold text-slate-500 uppercase tracking-widest mb-3 flex items-center gap-1.5"><Info size={12} /> Specifications</h4>
      <div className="grid grid-cols-2 gap-y-3 gap-x-4">
        {attributes.map((attr, idx) => (
          <div key={idx} className="flex flex-col">
            <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{attr.label}</span>
            <span className="text-sm font-medium text-slate-900 dark:text-white truncate" title={attr.value}>{attr.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── COMPACT SWIPE GALLERY (grid cards / small previews) ──────────────────
export function ImageGallery({ images, alt, height, fallbackEmoji = "📦" }) {
  const [idx, setIdx] = useState(0);
  const touchStartX = useRef(null);
  const hasImages = images && images.length > 0;
  const count = hasImages ? images.length : 0;

  const go = (dir) => {
    if (count < 2) return;
    setIdx((prev) => (prev + dir + count) % count);
  };

  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 40) go(delta > 0 ? -1 : 1);
    touchStartX.current = null;
  };

  if (!hasImages) {
    return (
      <div style={{ width: "100%", height, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 48 }}>
        {fallbackEmoji}
      </div>
    );
  }

  return (
    <div style={{ position: "relative", width: "100%", height, overflow: "hidden", touchAction: "pan-y" }} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      <img src={images[idx].image} alt={alt} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
      {count > 1 && (
        <>
          <button onClick={(e) => { e.stopPropagation(); go(-1); }} aria-label="Previous photo"
            style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", width: 32, height: 32, borderRadius: "50%", background: "rgba(0,0,0,0.45)", border: "none", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", backdropFilter: "blur(4px)" }}>
            <ChevronLeft size={18} />
          </button>
          <button onClick={(e) => { e.stopPropagation(); go(1); }} aria-label="Next photo"
            style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", width: 32, height: 32, borderRadius: "50%", background: "rgba(0,0,0,0.45)", border: "none", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", backdropFilter: "blur(4px)" }}>
            <ChevronRight size={18} />
          </button>
          <div style={{ position: "absolute", bottom: 8, left: "50%", transform: "translateX(-50%)", display: "flex", gap: 5, zIndex: 2 }}>
            {images.map((_, i) => (
              <div key={i} onClick={(e) => { e.stopPropagation(); setIdx(i); }}
                style={{ width: i === idx ? 16 : 6, height: 6, borderRadius: 99, background: i === idx ? "#fff" : "rgba(255,255,255,0.5)", cursor: "pointer", transition: "width .15s" }} />
            ))}
          </div>
          <div style={{ position: "absolute", top: 8, left: 8, padding: "2px 8px", borderRadius: 99, background: "rgba(0,0,0,0.55)", color: "#fff", fontSize: 10, fontWeight: 700, backdropFilter: "blur(4px)" }}>
            {idx + 1}/{count}
          </div>
        </>
      )}
    </div>
  );
}

// ─── FULL PRODUCT GALLERY (dedicated product page — main image + thumbnail rail) ──
export function ProductGallery({ images, alt, fallbackEmoji = "📦", isMobile }) {
  const [idx, setIdx] = useState(0);
  const touchStartX = useRef(null);
  const hasImages = images && images.length > 0;
  const count = hasImages ? images.length : 0;
  const mainHeight = isMobile ? 340 : 480;

  const go = (dir) => { if (count < 2) return; setIdx((p) => (p + dir + count) % count); };
  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 40) go(delta > 0 ? -1 : 1);
    touchStartX.current = null;
  };

  if (!hasImages) {
    return (
      <div style={{ width: "100%", height: mainHeight, borderRadius: isMobile ? 0 : 16, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 64, background: "var(--mkt-midbg)" }}>
        {fallbackEmoji}
      </div>
    );
  }

  return (
    <div>
      <div
        style={{ position: "relative", width: "100%", height: mainHeight, overflow: "hidden", borderRadius: isMobile ? 0 : 16, background: "var(--mkt-midbg)", touchAction: "pan-y" }}
        onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}
      >
        <img src={images[idx].image} alt={alt} style={{ width: "100%", height: "100%", objectFit: "contain" }} />
        {count > 1 && !isMobile && (
          <>
            <button onClick={() => go(-1)} aria-label="Previous photo"
              style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", width: 40, height: 40, borderRadius: "50%", background: "rgba(15,23,42,0.55)", border: "none", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", backdropFilter: "blur(4px)" }}>
              <ChevronLeft size={20} />
            </button>
            <button onClick={() => go(1)} aria-label="Next photo"
              style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", width: 40, height: 40, borderRadius: "50%", background: "rgba(15,23,42,0.55)", border: "none", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", backdropFilter: "blur(4px)" }}>
              <ChevronRight size={20} />
            </button>
          </>
        )}
        <div style={{ position: "absolute", bottom: 12, right: 12, padding: "3px 9px", borderRadius: 99, background: "rgba(15,23,42,0.65)", color: "#fff", fontSize: 11, fontWeight: 700, backdropFilter: "blur(4px)" }}>
          {idx + 1}/{count}
        </div>
      </div>
      {count > 1 && (
        <div className="hide-scrollbar" style={{ display: "flex", gap: 8, marginTop: 10, overflowX: "auto", padding: isMobile ? "0 14px" : 0 }}>
          {images.map((img, i) => (
            <button key={i} onClick={() => setIdx(i)}
              style={{ flexShrink: 0, width: 64, height: 64, borderRadius: 10, overflow: "hidden", border: i === idx ? "2px solid #f59e0b" : "2px solid transparent", opacity: i === idx ? 1 : 0.7, padding: 0, cursor: "pointer", background: "var(--mkt-midbg)" }}>
              <img src={img.image} alt={`${alt} ${i + 1}`} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── INLINE FLOATING DESKTOP CHAT (small widget, independent of details view) ──
export function InlineSidebarChat({ currentItem, onClose, onSubscriptionError }) {
  const [activeConvoId, setActiveConvoId] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(true);
  const bottomRef = useRef(null);
  const wsRef = useRef(null);
  const authToken = localStorage.getItem("token");
  const apiUrl = API_URL;
  const userStr = localStorage.getItem("user");
  let loggedInUser = {};
  try { if (userStr) loggedInUser = JSON.parse(userStr); } catch (e) {}
  const myId = loggedInUser?.id || loggedInUser?.pk;
  const myUsername = loggedInUser?.username || loggedInUser?.name || loggedInUser?.email || localStorage.getItem("username");

  useEffect(() => {
    const startConvo = async () => {
      try {
        const startRes = await fetch(`${apiUrl}/api/tradelink/chat/start/`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
          body: JSON.stringify({ listing_id: currentItem.id }),
        });
        if (startRes.status === 403) {
          const errData = await startRes.json();
          if (errData.code === "REQUIRES_SUBSCRIPTION" || errData.detail?.includes("membership")) {
            onSubscriptionError();
            return;
          }
        }
        if (startRes.ok) {
          const startData = await startRes.json();
          setActiveConvoId(startData.conversation_id);
        }
      } catch (err) {
        console.error("Failed to start chat:", err);
      } finally {
        setLoading(false);
      }
    };
    if (authToken && currentItem) startConvo();
  }, [currentItem, authToken, apiUrl, onSubscriptionError]);

  useEffect(() => {
    if (!activeConvoId) return;
    const fetchHistory = async () => {
      try {
        const res = await fetch(`${apiUrl}/api/tradelink/chat/${activeConvoId}/history/`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (res.ok) {
          const data = await res.json();
          setMsgs(data.results || data);
        }
      } catch (err) {}
    };
    fetchHistory();
    let wsBaseUrl = apiUrl.startsWith("https://") ? apiUrl.replace("https://", "wss://") : apiUrl.replace("http://", "ws://");
    const socketUrl = `${wsBaseUrl}/ws/chat/tradelink/${activeConvoId}/?token=${authToken}`;
    const chatSocket = new WebSocket(socketUrl);
    wsRef.current = chatSocket;
    chatSocket.onmessage = (event) => {
      const newMsg = JSON.parse(event.data);
      if (newMsg.code === "REQUIRES_SUBSCRIPTION") {
        onSubscriptionError();
        return;
      }
      setMsgs((prev) => {
        const existingIndex = prev.findIndex((m) => m.id === newMsg.id);
        if (existingIndex !== -1) {
          const updatedMsgs = [...prev];
          updatedMsgs[existingIndex] = { ...updatedMsgs[existingIndex], ...newMsg };
          return updatedMsgs;
        }
        const optimisticIndex = prev.findIndex((m) => m.isOptimistic && m.content === newMsg.content);
        if (optimisticIndex !== -1) {
          const updatedMsgs = [...prev];
          updatedMsgs[optimisticIndex] = { ...newMsg };
          return updatedMsgs;
        }
        return [...prev, newMsg];
      });
    };
    return () => { if (chatSocket.readyState === WebSocket.OPEN) chatSocket.close(); };
  }, [activeConvoId, authToken, apiUrl, onSubscriptionError]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const handleSend = async (textToSend) => {
    if (!textToSend.trim() || !activeConvoId) return;
    setInput("");
    const tempMsg = {
      id: `temp-${Date.now()}`, sender: myId, sender_name: myUsername, content: textToSend,
      is_read: false, isOptimistic: true, created_at: new Date().toISOString(),
    };
    setMsgs((prev) => [...prev, tempMsg]);
    try {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ message: textToSend }));
      } else {
        const res = await fetch(`${apiUrl}/api/tradelink/chat/send/`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
          body: JSON.stringify({ conversation_id: activeConvoId, text: textToSend }),
        });
        if (res.status === 403) onSubscriptionError();
      }
    } catch (err) { console.error("Failed to send:", err); }
  };

  const sellerName = formatName(currentItem.seller);
  return (
    <div className="border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden bg-slate-50 dark:bg-slate-800/50 flex flex-col h-[420px] w-full">
      <div className="flex items-center justify-between p-3 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <div className="h-7 w-7 rounded-full bg-cyan-100 text-cyan-700 flex items-center justify-center font-bold text-xs uppercase shrink-0">
            {getInitials(sellerName)}
          </div>
          <div className="min-w-0">
            <div className="text-sm font-bold text-slate-900 dark:text-white truncate max-w-[160px]">{sellerName}</div>
            <div className="text-[11px] text-slate-400 truncate max-w-[160px]">{currentItem.title}</div>
          </div>
        </div>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 shrink-0">
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-3 flex flex-col gap-2">
        {loading ? (
          <div className="flex-1 flex justify-center items-center text-sm text-slate-400">Connecting...</div>
        ) : msgs.length === 0 ? (
          <div className="mt-auto space-y-3">
            <div className="px-1"><Disclaimer variant="marketplace" /></div>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest text-center mb-2">Quick Questions</p>
            <div className="flex flex-wrap gap-1.5 justify-center">
              {QUICK_QUESTIONS.map((q) => (
                <button key={q} onClick={() => handleSend(q)} className="bg-white border border-slate-200 text-slate-600 text-xs px-2.5 py-1.5 rounded-full hover:bg-slate-50 hover:border-cyan-300 transition-colors">{q}</button>
              ))}
            </div>
          </div>
        ) : (
          msgs.map((m, index) => {
            const isMe = m.isOptimistic || Boolean(
              (myId && (String(m.sender) === String(myId) || String(m.sender_id) === String(myId))) ||
              (myUsername && m.sender_name && (String(m.sender_name).toLowerCase() === String(myUsername).toLowerCase()))
            );
            return (
              <div key={m.id || `msg-${index}`} className={`flex w-full ${isMe ? "justify-end" : "justify-start"}`}>
                <div className={`flex flex-col max-w-[85%] ${isMe ? "items-end" : "items-start"}`}>
                  <div className={`px-3 py-1.5 text-sm shadow-sm ${isMe ? "bg-cyan-600 text-white rounded-[12px_12px_4px_12px]" : "bg-white border border-slate-200 text-slate-900 rounded-[12px_12px_12px_4px]"}`}>{m.content}</div>
                  <div className="flex items-center gap-1 mt-0.5 px-1">
                    <span className="text-[9px] text-slate-400 font-medium">{new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                    {isMe && <StatusTick isRead={m.is_read} size={10} />}
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={bottomRef} />
      </div>
      <div className="p-2 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700 flex gap-2 shrink-0">
        <input value={input} onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && input.trim()) handleSend(input); }}
          placeholder="Type a message..."
          className="flex-1 bg-slate-100 dark:bg-slate-800 border-none rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-cyan-500 outline-none"
        />
        <button onClick={() => handleSend(input)} disabled={!input.trim()}
          className="bg-[#0891b2] hover:bg-[#06b6d4] disabled:opacity-50 text-white px-3 py-2 rounded-lg font-bold text-sm transition-colors flex items-center justify-center">
          <Send className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// ─── PREMIUM MESSAGING MODAL (full inbox — mobile + "view all messages") ──────
export function PremiumMessagingCenter({ currentItem, onClose, onSubscriptionError }) {
  const [activeConvoId, setActiveConvoId] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(true);
  const bottomRef = useRef(null);
  const wsRef = useRef(null);
  const authToken = localStorage.getItem("token");
  const apiUrl = API_URL;
  const userStr = localStorage.getItem("user");
  let loggedInUser = {};
  try { if (userStr) loggedInUser = JSON.parse(userStr); } catch (e) {}
  const myId = loggedInUser?.id || loggedInUser?.pk;
  const myUsername = loggedInUser?.username || loggedInUser?.name || loggedInUser?.email || localStorage.getItem("username");

  useEffect(() => {
    const fetchInboxAndStart = async () => {
      try {
        const inboxRes = await fetch(`${apiUrl}/api/tradelink/chat/inbox/`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (inboxRes.status === 403) {
          onSubscriptionError();
          return;
        }
        let fetchedConvos = [];
        if (inboxRes.ok) {
          const data = await inboxRes.json();
          fetchedConvos = Array.isArray(data) ? data : (data.results || []);
          setConversations(fetchedConvos);
        }
        if (currentItem) {
          const existingConvo = fetchedConvos.find((c) => c.listing_title === currentItem.title);
          if (existingConvo) {
            setActiveConvoId(existingConvo.id);
          } else {
            const startRes = await fetch(`${apiUrl}/api/tradelink/chat/start/`, {
              method: "POST",
              headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
              body: JSON.stringify({ listing_id: currentItem.id }),
            });
            if (startRes.status === 403) {
              onSubscriptionError();
              return;
            }
            const startData = await startRes.json();
            if (startRes.ok) {
              setActiveConvoId(startData.conversation_id);
              const newInboxRes = await fetch(`${apiUrl}/api/tradelink/chat/inbox/`, {
                headers: { Authorization: `Bearer ${authToken}` },
              });
              if (newInboxRes.ok) {
                const newData = await newInboxRes.json();
                setConversations(Array.isArray(newData) ? newData : (newData.results || []));
              }
            }
          }
        }
      } catch (err) { console.error(err); } finally { setLoading(false); }
    };
    if (authToken) fetchInboxAndStart();
  }, [currentItem, authToken, apiUrl, onSubscriptionError]);

  useEffect(() => {
    if (!activeConvoId) return;
    const fetchHistory = async () => {
      try {
        const res = await fetch(`${apiUrl}/api/tradelink/chat/${activeConvoId}/history/`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (res.ok) {
          const data = await res.json();
          setMsgs(data.results || data);
        }
      } catch (err) {}
    };
    fetchHistory();
    let wsBaseUrl = apiUrl.startsWith("https://") ? apiUrl.replace("https://", "wss://") : apiUrl.replace("http://", "ws://");
    const socketUrl = `${wsBaseUrl}/ws/chat/tradelink/${activeConvoId}/?token=${authToken}`;
    const chatSocket = new WebSocket(socketUrl);
    wsRef.current = chatSocket;
    chatSocket.onmessage = (event) => {
      const newMsg = JSON.parse(event.data);
      if (newMsg.code === "REQUIRES_SUBSCRIPTION") {
        onSubscriptionError();
        return;
      }
      setMsgs((prev) => {
        const existingIndex = prev.findIndex((m) => m.id === newMsg.id);
        if (existingIndex !== -1) {
          const updatedMsgs = [...prev];
          updatedMsgs[existingIndex] = { ...updatedMsgs[existingIndex], ...newMsg };
          return updatedMsgs;
        }
        const optimisticIndex = prev.findIndex((m) => m.isOptimistic && m.content === newMsg.content);
        if (optimisticIndex !== -1) {
          const updatedMsgs = [...prev];
          updatedMsgs[optimisticIndex] = { ...newMsg };
          return updatedMsgs;
        }
        return [...prev, newMsg];
      });
    };
    return () => { if (chatSocket.readyState === WebSocket.OPEN) chatSocket.close(); };
  }, [activeConvoId, authToken, apiUrl, onSubscriptionError]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, activeConvoId]);

  const sendMessage = async () => {
    if (!input.trim() || !activeConvoId) return;
    const text = input.trim();
    setInput("");
    const tempMsg = {
      id: `temp-${Date.now()}`, sender: myId, sender_name: myUsername, content: text,
      is_read: false, isOptimistic: true, created_at: new Date().toISOString(),
    };
    setMsgs((prev) => [...prev, tempMsg]);
    try {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ message: text }));
      } else {
        const res = await fetch(`${apiUrl}/api/tradelink/chat/send/`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
          body: JSON.stringify({ conversation_id: activeConvoId, text }),
        });
        if (res.status === 403) onSubscriptionError();
      }
    } catch (err) {}
  };

  const activeConvoData = conversations.find((c) => c.id === activeConvoId);
  const partnerName = activeConvoData
    ? formatName({ first_name: activeConvoData.partner_first_name, last_name: activeConvoData.partner_last_name, name: activeConvoData.partner_name, email: activeConvoData.partner_email })
    : formatName(currentItem?.seller);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl w-full max-w-5xl h-[85vh] shadow-2xl flex overflow-hidden border border-slate-200" onClick={(e) => e.stopPropagation()}>
        <div className={`w-full md:w-1/3 bg-slate-50 border-r border-slate-200 flex flex-col ${activeConvoId ? "hidden md:flex" : "flex"}`}>
          <div className="p-5 border-b border-slate-200 bg-white flex justify-between items-center">
            <h2 className="text-xl font-extrabold text-slate-900 tracking-tight">Messages</h2>
            <button onClick={onClose} className="md:hidden text-slate-400 hover:text-slate-600"><X className="w-6 h-6" /></button>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading ? (<div className="p-8 text-center text-slate-400 font-medium animate-pulse">Loading inbox...</div>) : conversations.map((convo) => {
              const rowName = formatName({ first_name: convo.partner_first_name, last_name: convo.partner_last_name, name: convo.partner_name, email: convo.partner_email });
              return (
                <div key={convo.id} onClick={() => setActiveConvoId(convo.id)} className={`p-4 border-b border-slate-200 cursor-pointer transition-all ${activeConvoId === convo.id ? "bg-blue-50 border-l-4 border-l-blue-500" : "bg-white hover:bg-slate-100 border-l-4 border-l-transparent"}`}>
                  <div className="flex justify-between items-start mb-1">
                    <div className="font-bold text-slate-900 text-sm truncate pr-2">{convo.listing_title}</div>
                    {convo.unread_count > 0 && (
                      <div className="bg-blue-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">{convo.unread_count}</div>
                    )}
                  </div>
                  <div className="text-xs font-medium text-slate-500 mt-1">{rowName}</div>
                </div>
              );
            })}
          </div>
        </div>
        {activeConvoId && (
          <div className={`flex-1 flex flex-col bg-white relative ${!activeConvoId ? "hidden md:flex" : "flex"}`}>
            <div className="px-4 md:px-6 py-4 border-b border-slate-200 flex justify-between items-center bg-white z-10">
              <div className="flex items-center gap-3">
                <button onClick={() => setActiveConvoId(null)} className="md:hidden w-8 h-8 flex items-center justify-center text-slate-500 hover:bg-slate-100 rounded-full"><ArrowLeft className="w-5 h-5" /></button>
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-cyan-100 text-cyan-700 flex items-center justify-center font-extrabold text-base md:text-lg border-2 border-cyan-200">{getInitials(partnerName)}</div>
                <div className="font-bold text-slate-900 text-base md:text-lg">{partnerName}</div>
              </div>
              <button onClick={onClose} className="w-8 h-8 flex items-center justify-center bg-slate-50 hover:bg-slate-100 text-slate-400 rounded-full border border-slate-200"><X className="w-4 h-4" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 md:p-6 bg-[#f0f2f5] flex flex-col gap-3">
              {msgs.map((m, index) => {
                const isMe = m.isOptimistic || Boolean(
                  (myId && (String(m.sender) === String(myId) || String(m.sender_id) === String(myId))) ||
                  (myUsername && m.sender_name && (String(m.sender_name).toLowerCase() === String(myUsername).toLowerCase()))
                );
                return (
                  <div key={m.id || `msg-${index}`} className={`flex w-full ${isMe ? "justify-end" : "justify-start"}`}>
                    <div className={`flex flex-col max-w-[85%] md:max-w-[75%] ${isMe ? "items-end" : "items-start"}`}>
                      <div className={`px-4 py-2 text-sm md:text-[15px] shadow-sm ${isMe ? "bg-cyan-600 text-white rounded-[16px_16px_4px_16px]" : "bg-white border border-slate-200 text-slate-900 rounded-[16px_16px_16px_4px]"}`}>{m.content}</div>
                      <div className="flex items-center gap-1.5 mt-1 px-1">
                        <span className="text-[10px] text-slate-400 font-medium">{new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                        {isMe && <StatusTick isRead={m.is_read} />}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={bottomRef} />
            </div>
            <div className="p-3 bg-[#f0f2f5] border-t border-slate-200">
              <div className="flex items-end gap-2 max-w-4xl mx-auto bg-white p-2 rounded-[24px] shadow-sm border border-slate-200">
                <textarea value={input} onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                  placeholder="Message..." className="flex-1 bg-transparent px-4 py-2 outline-none text-slate-800 text-[15px] resize-none" style={{ minHeight: "40px", maxHeight: "100px" }}
                />
                <button onClick={sendMessage} disabled={!input.trim()} className="bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 text-white rounded-full h-10 w-10 flex items-center justify-center shrink-0"><Send className="w-4 h-4 ml-0.5" /></button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
